<script setup>
    import Layout from "@/layouts/main.vue";
    import PageHeader from "@/components/page-header";
    import '@jobinsjp/vue3-datatable/dist/style.css';
    import { DataTable, TableBody, TableHead, TableBodyCell } from '@jobinsjp/vue3-datatable';
    import moment from 'moment';
    import { truncate } from 'lodash';
    import Swal from 'sweetalert2';
    import axios from 'axios';
    import { computed, ref } from 'vue';
    import { useStore } from 'vuex';
    import { authHeader } from './../../../helpers/authservice/auth-header';
    import { required, helpers } from "@vuelidate/validators";
    import useVuelidate from "@vuelidate/core";


    const store = useStore();
    const loading = computed(() => store.state.globals.loading);
    const mvTypes = computed(() => store.state.mvType.mvTypes);
    const pagination = computed(() => store.state.mvType.pagination);
    const notification = computed(() => store.state.notification);
    const title = ref("MV Types");
    const items = ref([
        {
            text: "Home",
            href: "/"
        },
        {
            text: "Settings",
        },
        {
            text: "MV Types",
            active: true,
        }
    ]);

    const query = ref({
        search: ''        
    });

    const rules = computed(() => ({
        name: {
            required: helpers.withMessage("Name is required", required),
        },
        description: {
            required: helpers.withMessage("Description is required", required),
        },
        remarks: {
            required: helpers.withMessage("Remarks is required", required),
        }
    }));

    const submitted = ref(false);

    const mvType = ref({
        id: '',
        name: '',
        description: '',
        remarks: '',
        updated_at: null
    })

    const v$ = useVuelidate(rules, mvType);

    const getAllMVTypes = async (dt) => {
        await store.dispatch('mvType/getMVTypes', {
            page: dt.page,
            pageSize: dt.per_page ,
            sortColumn: 'updated_at',
            search: dt.search,
            orderBy: 'desc'
        });
    }

    const formattedDate = (date) => {
        if (!date) return 'N/A';
        return moment(date).format('YYYY-MM-DD h:mm A')
    }

    const deleteMVType = (id, name) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#34c38f",
            cancelButtonColor: "#f46a6a",
            confirmButtonText: "Delete",
        }).then(async (result) => {
            if (result.value) {
                await store.dispatch('mvType/deleteMVType', { id, name });
            }
        });
    }

    const getMVTypeDetails = async (id) => {
        store.dispatch('globals/loading', null, { root: true });

        try {
            const { data } = await axios.get(`/mvtype/${id}`, {
                headers: authHeader()
            });
            mvType.value = data.data;
            store.dispatch('notification/clear', null, { root: true });
            store.dispatch('globals/loaded', null, { root: true });
        } catch (err) {
            console.log(err);
            store.dispatch('globals/loaded', null, { root: true });
        }
    }

    const updateMVTypeDetails = async () => {
        try {
            submitted.value = true
            v$.value.$touch();
            if (v$.value.$invalid) return;
            await store.dispatch('mvType/updateMVType', mvType.value)
        } catch (err) {
            console.log(err);
        }
    }
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <b-overlay :show="loading">
        <div class="d-flex mb-4 justify-content-between">
          <div>
            <h1 class="fs-4 text">MV Types</h1>
            <small>Set the type of motor vehicles.</small>
          </div>
          <div>
             <router-link
                  to="/settings/mv-type/create"
                  class="btn btn-primary right"
                >
                 <i class="ri-add-fill label-icon align-middle fs-16 me-2"></i>
                  New MV Type
              </router-link>
          </div>
        </div>

        <div class="row">
            <div class="col lg-12">
                <data-table
                    :rows="mvTypes"
                    :pagination="pagination"
                    striped
                    :loading="loading"
                    :query="query"
                    filter
                    hoverable
                    sortable
                    @loadData="getAllMVTypes"
                    >

                    <template #thead>
                        <table-head>Name</table-head>
                        <table-head>Description</table-head>
                        <table-head>Remarks</table-head>
                        <table-head>Date Modified</table-head>
                        <table-head>Actions</table-head>
                    </template>

                    <template #tbody="{row}">
                        <table-body v-text="truncate(row.name, { length: 30 })" :title="row.name" />
                        <table-body v-text="truncate(row.description ? row.description : 'N/A', { length: 35 })" :title="row.description" />
                        <table-body v-text="truncate(row.remarks ? row.remarks : 'N/A', { length: 35 })" :title="row.remarks" />
                        <table-body v-text="formattedDate(row.updated_at)" />
                        <table-body>
                        <button
                            class="btn btn-info mr-3"
                            title="Edit"
                            data-bs-toggle="modal"
                            data-bs-target="#editMVTypeModal"
                            @click="getMVTypeDetails(row.id)"
                        >
                            <i class="ri-pencil-line label-icon align-middle"></i>
                        </button>
                        <button class="btn btn-danger" type="button" title="Delete" @click.prevent="deleteMVType(row.id, row.name)">
                            <i class="mdi mdi-delete label-icon align-middle"></i>
                        </button>
                        </table-body>
                    </template>

                    <template #empty>
                    <TableBodyCell colspan="10">
                        No MV Types found.
                    </TableBodyCell>
                </template>
                </data-table>
            </div>
        </div>
      </b-overlay>
  </Layout>
  <!-- Varying modal content -->
  <div class="modal fade" role="dialog" id="editMVTypeModal" tabindex="-1" aria-labelledby="varyingcontentModalLabel" aria-hidden="true">
      <div class="modal-dialog">
          <div class="modal-content">
              <b-overlay :show="loading">
                <div class="modal-header">
                    <h5 class="modal-title" id="varyingcontentModalLabel">MV Type Details</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                      <form>
                          <b-alert v-if="notification && notification.message"
                              show variant="success" class="fade show" role="alert">
                              {{ notification.message }}
                          </b-alert>

                          <div v-if="notification && notification.messages.length > 0">
                              <b-alert v-for="(message, index) in notification.messages" :key="index"
                                  show variant="danger" class="fade show" role="alert">
                                  {{ message }}
                              </b-alert>
                          </div>
                          <div class="mb-3">
                              <label for="name" class="form-label required">Name</label>
                              <input 
                                :class="{ 'is-invalid': submitted && v$.name.$error }"
                                type="text" class="form-control" id="name" v-model="mvType.name">
                              <div
                                  v-for="item of v$.name.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                              >
                                <span v-if="item.$message">{{ item.$message }}</span>
                              </div>
                          </div>
                          <div class="mb-3">
                              <label for="description" class="form-label required">Description</label>
                              <textarea 
                                id="description"
                                :class="{ 'is-invalid': submitted && v$.description.$error }"
                                class="form-control" v-model="mvType.description"></textarea>
                               <div
                                  v-for="item of v$.description.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                              >
                                <span v-if="item.$message">{{ item.$message }}</span>
                              </div>
                          </div>
                          <div class="mb-3">
                              <label for="remarks" class="form-label required">Remarks</label>
                              <textarea 
                                :class="{ 'is-invalid': submitted && v$.remarks.$error }"
                                class="form-control" id="remarks" v-model="mvType.remarks"></textarea>
                               <div
                                  v-for="item of v$.remarks.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                              >
                                <span v-if="item.$message">{{ item.$message }}</span>
                              </div>
                          </div>
                      </form>
                       <div class="text-muted">
                          Last Updated: {{ formattedDate(mvType.updated_at) }}
                        </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-danger" data-bs-dismiss="modal">
                      <i class="mdi mdi-close-thick"></i>
                      Close
                    </button>
                    <button @click.prevent="updateMVTypeDetails" type="button" class="btn btn-success">
                      <i class="mdi mdi-content-save"></i>
                      Update MV Type
                    </button>
                </div>
              </b-overlay>
          </div>
      </div>
    </div>
</template>